import ApiService from './api.service';

export type VideoStatus = 'pending' | 'transcript_ready' | 'ready' | 'failed';
export type ChaptersStatus = 'not_requested' | 'generating' | 'ready' | 'failed';

export type TranscriptSegment = {
  start_ms: number;
  end_ms: number;
  speaker: string | null;
  text: string;
};

export type Chapter = {
  start_ms: number;
  end_ms: number;
  title: string;
  summary: string | null;
};

export type FindingsStatus = 'not_requested' | 'generating' | 'ready' | 'failed';
export type FindingCategory = 'knowledge' | 'gap' | 'task';
export type FindingStatus = 'proposed' | 'accepted' | 'declined';
export type FindingTargetType = 'knowledge_base_item' | 'semantic_gap' | 'task';

export type Finding = {
  id: string;
  category: FindingCategory;
  title: string;
  summary: string | null;
  start_ms: number;
  end_ms: number;
  speaker: string | null;
  gap_type_hint: string | null;
  status: FindingStatus;
  created_object_type: string | null;
  created_object_id: string | null;
  clip_id: string | null;
};

export type AcceptFindingPayload = {
  target_type: FindingTargetType;
  include_clip?: boolean;
  payload?: Record<string, any>;
};

export type VideoSource = {
  id: string;
  provider: string;
  external_id: string | null;
  source_url: string;
  title: string;
  duration_ms: number;
  s3_key: string | null;
  transcript: TranscriptSegment[];
  chapters: Chapter[] | null;
  chapters_status: ChaptersStatus;
  chapters_status_detail: string | null;
  findings: Finding[] | null;
  findings_status: FindingsStatus;
  findings_status_detail: string | null;
  status: VideoStatus;
  status_detail: string | null;
  created_by: string;
  created_at: string;
};

export type Clip = {
  id: string;
  video_source_id: string;
  start_ms: number;
  end_ms: number;
  title: string;
  summary: string | null;
  created_by: string;
  created_by_message_id: string | null;
  created_at: string;
};

export type ClipStreamUrl =
  | {
      ready: true;
      url: string;
      video_source_id: string;
      start_ms: number;
      end_ms: number;
      title: string;
    }
  | {
      ready: false;
      status: VideoStatus;
      status_detail: string | null;
    };

export type VideoStreamUrl =
  | { ready: true; url: string; duration_ms: number }
  | { ready: false; status: VideoStatus; status_detail: string | null };

export type UpdateFindingPayload = Partial<{
  title: string;
  summary: string | null;
  speaker: string | null;
  start_ms: number;
  end_ms: number;
  category: FindingCategory;
  gap_type_hint: string | null;
}>;

export type CreateClipPayload = {
  video_source_id: string;
  start_ms: number;
  end_ms: number;
  title: string;
  summary?: string | null;
};

export class VideoService {
  async createClip(payload: CreateClipPayload): Promise<Clip> {
    const result = (await ApiService.getInstance().request(
      'POST',
      '/clips',
      payload,
    )) as { record: Clip };
    return result.record;
  }

  async getVideoSource(id: string): Promise<VideoSource> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/video-sources/${id}`
    )) as { record: VideoSource };
    return result.record;
  }

  async getClip(id: string): Promise<Clip> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/clips/${id}`
    )) as { record: Clip };
    return result.record;
  }

  async getClipStreamUrl(id: string): Promise<ClipStreamUrl> {
    // 409 indicates not-ready; ApiService treats non-2xx as error so this
    // path may throw — callers should handle it via React-Query's error state.
    return (await ApiService.getInstance().request(
      'GET',
      `/clips/${id}/stream-url`
    )) as ClipStreamUrl;
  }

  async getVideoStreamUrl(id: string): Promise<VideoStreamUrl> {
    // 409 when media isn't cached yet.
    return (await ApiService.getInstance().request(
      'GET',
      `/video-sources/${id}/stream-url`,
    )) as VideoStreamUrl;
  }

  async updateFinding(
    videoSourceId: string,
    findingId: string,
    patch: UpdateFindingPayload,
  ): Promise<Finding> {
    const result = (await ApiService.getInstance().request(
      'PATCH',
      `/video-sources/${videoSourceId}/findings/${findingId}`,
      patch,
    )) as { record: Finding };
    return result.record;
  }

  async acceptFinding(
    videoSourceId: string,
    findingId: string,
    payload: AcceptFindingPayload,
  ): Promise<{
    record: Finding;
    created_object_type: string;
    created_object_id: string;
    clip_id: string | null;
  }> {
    return (await ApiService.getInstance().request(
      'POST',
      `/video-sources/${videoSourceId}/findings/${findingId}/accept`,
      payload,
    )) as any;
  }

  async declineFinding(videoSourceId: string, findingId: string): Promise<{ record: Finding }> {
    return (await ApiService.getInstance().request(
      'POST',
      `/video-sources/${videoSourceId}/findings/${findingId}/decline`,
    )) as { record: Finding };
  }
}

export const videoService = new VideoService();
